import { Autocomplete, Box, Checkbox, css, FormControlLabel, Grid, Slider, Stack, TextField, Typography } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';

// import { UseLayerDashboardStore } from '../stores/useLayerDashboardStore';
import { StateFilter } from '../types/StateFilter';
import { eliminateArrayDuplicates } from 'utils/arrayUtilts';
import { createModelFilterByDisplaybleData } from '../utils/filterUtils';
import { TitleChart } from './TitleChart';
import { FilterType } from '../enums/FilterType';
import { useLayerDashboardTabStore } from '../context/LayerDashboardContext';

interface IAttributeProp {
    name: string;
    label: string;
    type: FilterType;
    min: number,
    max: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    values: any[]
}

const styles = css({
    overflow: 'auto',
    height: '100%',
    boxSizing: 'border-box',

    '& .item-filter': {
        marginBottom: 0,
        padding: 5,
        borderRadius: 5
    },
    '& .container-filter': {
        overflow: 'auto'
    },
    '& .item-filter:hover': {
        backgroundColor: '#9ebbd7'
    },
    '& .item-filter-button': {
        border: '1px solid #ccc',
        borderRadius: '6px',
        textAlign: 'center',
        backgroundColor: 'white',
        cursor: 'pointer',
        transition: 'background-color 0.2s ease',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '50px',
    },
    '& .item-filter-button:hover': {
        backgroundColor: '#f5f5f5'
    },
    '& .item-filter-button-active': {
        backgroundColor: '#e0e0e0',
        borderColor: '#999'
    },
    '& .item-filter-checkbox': {
        margin: 0,
        '.MuiTypography-root': {
            fontSize: '0.85rem'
            // marginBottom: -10,
        },
    }
});

export const Filter = () => {
    const { attributes, data, displayableData, setDisplayableData } = useLayerDashboardTabStore(state => ({
        attributes: state.attributes,
        displayableData: state.displayableData,
        data: state.data,
        setDisplayableData: state.setDisplayableData,
    }));

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [stateFilter, setStateFilter] = useState<StateFilter>({});

    const [stateRangeFilter, setStateRangeFilter] = useState<StateFilter>({});

    const changedByComponent = useRef<boolean>(false);

    useEffect(() => {
        const filteredData = data.filter((x) => {
            for (let property in stateFilter) {
                const array = stateFilter[property];

                if (array.length === 0) {
                    continue;
                }

                const attribute = attributes.find(x => x.Name === property);

                if (!attribute) {
                    console.error('atributo não encontrado', property, attributes);
                    return false;
                }

                if (attribute.IsDataToPlot) {
                    if (array.length !== 2) {
                        console.error('atributo ' + attribute.Label + ' não tem array compativel de tamanho 2', array);
                        return false;
                    }
                    const min = array[0];
                    const max = array[1];

                    if (x[property] >= min && x[property] <= max) {
                        continue;
                    }

                    return false;
                }
                else {
                    let hasValue = false;
                    for (let value of array) {
                        if (x[property] === value) {
                            hasValue = true;
                            break;
                        }
                    }

                    if (!hasValue) {
                        return false;
                    }
                }
            }

            return true;
        });

        setDisplayableData(filteredData);
    }, [stateFilter]);

    useEffect(() => {
        if (changedByComponent.current) {
            changedByComponent.current = false;
            return;
        }

        if (data.length === displayableData.length) {
            setStateFilter({});
            setStateRangeFilter({});
        }
        //setStateFilter(createModelFilterByDisplaybleData(data, displayableData, attributes));
    }, [JSON.stringify(displayableData)]);

    const selectFilterType = (filterType: number, isDataToPlot: boolean) => {
        switch (filterType) {
            case 1:
                return FilterType.text;
            case 2:
                return FilterType.slider;
            case 3:
                return FilterType.button;
            case 4:
                return FilterType.list;
            case 5:
                return FilterType.dropdown;
            case 6:
                return FilterType.checkbox;
            case 0:
            default:
                if (isDataToPlot) {
                    return FilterType.slider;
                }
                return FilterType.text;
        }
    };

    const valuesToFilter = useMemo(() => {
        const attributesFilter = attributes.filter(x => x.IsToFilter || x.IsDataToPlot);
        const dictionary: { [label: string]: IAttributeProp } = {};

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let stateFilterValues: any[] = data;

        let attributePreserved = attributes.find(x => x.PreservedValue)?.Name;

        if (attributePreserved && stateFilter[attributePreserved] && stateFilter[attributePreserved].length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let filteredItens: any[] = [];
            for (let filter in stateFilter[attributePreserved]) {
                filteredItens = filteredItens.concat(stateFilterValues.filter(x => x[attributePreserved!] === stateFilter[attributePreserved!][filter]));
            }

            stateFilterValues = filteredItens;
        }

        for (const attribute of attributesFilter) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            let values: any[] = [];
            values = eliminateArrayDuplicates(stateFilterValues.map(item => item[attribute.Name])).filter(x => x !== null);
            if (values.length === 0 || (values.length === 1 && values[0] === undefined)) {
                continue;
            }
            if (attributePreserved && attribute.Name === attributePreserved) {
                values = eliminateArrayDuplicates(data.map(item => item[attribute.Name])).filter(x => x !== null);
            }
            let min = 0;
            let max = 0;

            if (attribute.IsDataToPlot && values.length > 0 && typeof values[0] === 'number') {
                min = Math.min(...values);
                max = Math.max(...values);
            }
            else {
                values = values.sort();
            }

            dictionary[attribute.Label] = {
                label: attribute.Label,
                name: attribute.Name,
                type: selectFilterType(attribute.FilterType, attribute.IsDataToPlot),
                min: min,
                max: max,
                values: values
            };
        }
        return Object.keys(dictionary).sort().map(x => dictionary[x]);
    }, [JSON.stringify(data), JSON.stringify(stateFilter)]);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onChange = (property: string, values: any[]) => {
        changedByComponent.current = true;

        setStateFilter(state => ({
            ...state,
            [property]: values
        }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onSliderChange = (property: string, values: any[]) => {
        setStateRangeFilter(state => ({
            ...state,
            [property]: values
        }));
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onButtonClick = (property: string, value: any) => {
        const currentValues = stateFilter[property] ?? [];
        const isActive = currentValues.length === 1 && currentValues[0] === value;

        const newValues = isActive ? [] : [value];

        onChange(property, newValues);
    };

    const onCheckboxMark = (property: string, item: string) => {
        const currentValues = stateFilter[property] || [];
        const isChecked = currentValues.includes(item);

        const newValues = isChecked
            ? currentValues.filter(v => v !== item)
            : [...currentValues, item];

        onChange(property, newValues);
    };

    return (
        <Stack css={styles}>
            <TitleChart title='Filter' maximizeOperation={''} />
            <div className='container-filter'> {
                valuesToFilter.map((x: IAttributeProp, idx: number) => {
                    if (x.type === FilterType.slider) {
                        return <Stack className='item-filter' key={x.name} direction={'row'} >
                            <Box width={'50%'} display={'flex'} >
                                <Typography>{x.label}</Typography>
                            </Box>
                            <Box width={'70%'} css={{}} style={{ padding: '0 30px' }}>
                                <Slider size='small' valueLabelDisplay="auto" max={x.max} min={x.min}
                                    value={stateRangeFilter[x.name] ?? [x.min, x.max]}
                                    onChange={(evt, values) => onSliderChange(x.name, values as number[])}
                                    onChangeCommitted={(evt, values) => {
                                        onChange(x.name, values as number[]);
                                        onSliderChange(x.name, values as number[]);
                                    }}
                                    step={0.01}
                                    marks={[{ value: x.min, label: x.min }, { value: x.max, label: x.max }]} />
                            </Box>
                        </Stack>;
                    }
                    else if (x.type === FilterType.button) {
                        // eslint-disable-next-line no-lone-blocks
                        return <Stack className='item-filter' key={x.name} direction={'column'}>
                            <Box width={'50%'} display={'flex'} >
                                <Typography>{x.label}</Typography>
                            </Box>
                            <Grid container spacing={2}>
                                {x.values.map((item) => {
                                    const isActive = stateFilter[x.name]?.includes(item);

                                    return (
                                        <Grid item xs={12} sm={4} key={item}>
                                            <Box className={`item-filter-button ${isActive ? 'item-filter-button-active' : ''}`} onClick={() => onButtonClick(x.name, item)}>
                                                <Typography>{item}</Typography>
                                            </Box>
                                        </Grid>
                                    );
                                }
                                )}
                            </Grid>
                        </Stack>;
                    }
                    else if (x.type === FilterType.list) {
                        return <Stack className='item-filter' key={x.name} direction={'column'}>
                            <Box width={'50%'} display={'flex'} >
                                <Typography>{x.label}</Typography>
                            </Box>
                            <Stack direction='column'>
                                <Grid container spacing={0}>
                                    {x.values.map((item) => {
                                        const isActive = stateFilter[x.name]?.includes(item);

                                        return (
                                            <Grid item xs={12} sm={3} key={item}>
                                                <FormControlLabel
                                                    key={item}
                                                    className='item-filter-checkbox'
                                                    control={
                                                        <Checkbox
                                                            checked={!!isActive}
                                                            onChange={() => onCheckboxMark(x.name, item)}
                                                            sx={{
                                                                p: 0.1, // reduz padding (padrão é 1.125rem = 9px)
                                                            }}
                                                        />
                                                    }
                                                    label={item}
                                                />
                                            </Grid>
                                        );
                                    }
                                    )}
                                </Grid>
                            </Stack>
                        </Stack>;
                    }
                    // else if (x.type === FilterType.dropdown) {

                    // }
                    // else if (x.type === FilterType.checkbox) {

                    // }
                    return <Autocomplete
                        size='small'
                        className='item-filter'
                        value={stateFilter[x.name] ?? []}
                        key={x.name}
                        multiple
                        options={x.values}
                        onChange={(evt, values) => onChange(x.name, values)}
                        renderInput={(params) => (
                            <TextField {...params} key={x.name} label={x.label} placeholder={x.label} />
                        )}
                    />;
                })
            }</div>
        </Stack>
    );
};